import { GraphQLSchema, parse } from 'graphql';
import _, { debounce, omit } from 'lodash';
import { equals } from 'lodash/fp';
import React, { useCallback, useState } from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';

import { ExplorerBehavioralFlags } from '../../../explorerBehavioralFlags';
import { getDocumentDefinitions } from '../../../helpers/graphqlParseHelpers';
import { OperationPath } from '../../../helpers/operationPathHelpers';
import { useHandleOpenSavedOperation } from '../../useHandleOpenSavedOperation';
import {
  GraphIdentifier,
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
  useReadFromPerGraphIdentifierLocalStorage,
  useWriteToPerGraphIdentifierLocalStorage,
} from '../../usePerGraphIdentifierLocalStorage';
import { getDefaultTabState } from '../getDefaultTabState';
import { DocsNavigationAction, useDocsPath } from '../useDocsPath';
import {
  ExplorerError,
  ExplorerResponse,
  ExplorerResult,
  useExplorerResponses,
} from '../useExplorerResponses';
import {
  HeaderEntry,
  INITIAL_HEADERS,
} from '../useHeadersManagerContext/shared';
import { useHeadersManagerContext } from '../useHeadersManagerContext/useHeadersManagerContext';

import {
  CollectionEntryIdState,
  CollectionEntryState,
  SharedCollectionEntryState,
} from './collectionEntryState';
import { generateOperationDisplayName } from './generateOperationDisplayName';
import { generateTabId } from './generateTabId';
import {
  SavedOperationAction,
  SavedOperationLocalState,
  useSavedOperationLocalState,
} from './useSavedOperationLocalState';

export type FileVariable = {
  variableKey: string;
  files: FileList | undefined;
  isMultiFile: boolean;
};

export interface EditorTabStateItem {
  id: string;
  displayName: string | undefined;
  operation: string;
  variables: string;
  fileVariables: Array<FileVariable>;
  headers: Array<HeaderEntry>;
  script: string;
}

type ResultAction =
  | { type: 'setResult'; result: ExplorerResponse | ExplorerError }
  | { type: 'setLoading' }
  | { type: 'resetToLastResult' };
export interface EditorTabState {
  // Tab management actions
  createNewTab: (operationState: CollectionEntryState | null) => string;
  closeTab: (tabId: string) => void;
  setSelectedTab: (tabId: string) => void;

  // Tab state metadata
  selectedTab: string | undefined;
  tabIds: string[];
  setTabIds: (tabIds: string[]) => void;

  // Getters/setters for tab specific data
  getInitialOperationByTab: (tabId: string) => string | undefined;
  setOperationByTab: (tabId: string, newValue: string) => void;
  getInitialVariablesByTab: (tabId: string) => string | undefined;
  setVariablesByTab: (tabId: string, newValue: string) => void;
  tabbedFileVariableValues: Record<string, FileVariable[]>;
  setFileVariableValuesForTab: (
    tabId: string,
    newFileVariables: FileVariable[],
  ) => void;
  getInitialPreflightOperationScriptByTab: (
    tabId: string,
  ) => string | undefined;
  setPreflightOperationScriptByTab: (tabId: string, newValue: string) => void;
  getInitialPostflightOperationScriptByTab: (
    tabId: string,
  ) => string | undefined;
  setPostflightOperationScriptByTab: (tabId: string, newValue: string) => void;
  result: ExplorerResult;
  getInitialMockedResponseByTab: (tabId: string) => string | undefined;
  setMockedResponseByTab: (tabId: string, newValue: string) => void;
  dispatchResultAction: (action: ResultAction) => void;

  // savedOperationLocalStateAction exposed in a separate less stable context
  dispatchSavedOperationLocalStateAction: (
    action: SavedOperationAction,
  ) => void;
  docsPath: OperationPath;
  baseDispatchDocsNavAction: (action: DocsNavigationAction) => void;
  savedOperationLocalState: SavedOperationLocalState;
}

const STABLE_EMPTY_STRING_ARRAY: string[] = [];
const STABLE_EMPTY_FILE_VARIABLE_OBJECT: Record<string, FileVariable[]> = {};
const STABLE_EMPTY_STRING_OBJECT: Record<string, string> = {};
const STABLE_EMPTY_HEADERS_OBJECT: Record<string, HeaderEntry[]> = {};

const EditorTabStateContext = React.createContext<EditorTabState | undefined>(
  undefined,
);
export function useEditorTabState() {
  const context = React.useContext(EditorTabStateContext);
  if (!context) {
    throw new Error(
      'useEditorTabState must be used within an EditorTabStateContext',
    );
  }
  return context;
}

export const TabbedSavedOperationLocalStateContext = React.createContext<
  SavedOperationLocalState | undefined
>(undefined);
export function useTabbedSavedOperationLocalState() {
  const savedOperationLocalState = React.useContext(
    TabbedSavedOperationLocalStateContext,
  );
  if (!savedOperationLocalState) {
    throw new Error(
      'useTabbedSavedOperationLocalState must be used within an TabbedSavedOperationLocalStateContext',
    );
  }
  return { savedOperationLocalState };
}

export const TabbedDisplayNamesContext = React.createContext<
  Record<string, string | undefined> | undefined
>(undefined);
export function useTabbedDisplayNames() {
  const tabbedDisplayNames = React.useContext(TabbedDisplayNamesContext);
  if (!tabbedDisplayNames) {
    throw new Error(
      'useTabbedDisplayNames must be used within an TabbedDisplayNamesContext',
    );
  }
  return { tabbedDisplayNames };
}

// This is set up as a context provider instead of hooks for performance
// reasons. We would need a hook to be able to fire on each keypress but not
// cause rerenders of the full (currently non memoed) Explorer tree. We can
// acheive this by splitting out less stable pieces (ed headers names) of
// information into their own providers and only calling the stable information
// from the root.
export const EditorTabStateProviders = ({
  children,
  graphIdentifier,
  schema,
  explorerBehavioralFlags,
}: {
  children: React.ReactNode;
  graphIdentifier: GraphIdentifier;
  schema: GraphQLSchema;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
}) => {
  /**
   * File variables are stored in local state only,
   * we don't store them in local storage
   * or per operation, they are ephemeral
   */
  const [tabbedFileVariables, setTabbedFileVariables] = useState<
    Record<string, FileVariable[]>
  >(STABLE_EMPTY_FILE_VARIABLE_OBJECT);
  const setFileVariableValuesForTab = useCallback(
    (tabId: string, newFileVariables: FileVariable[]) => {
      setTabbedFileVariables((currentFileVariables) => ({
        ...currentFileVariables,
        [tabId]: newFileVariables,
      }));
    },
    [],
  );

  const trackCustomEvent = useTrackCustomEvent();
  const [selectedTab, baseSetSelectedTab] = usePerGraphIdentifierLocalStorage({
    key: 'selectedTab',
    graphIdentifier,
    stableInitialValue: undefined,
  });

  const [docsPath, baseDispatchDocsNavAction] = useDocsPath(
    schema,
    selectedTab ?? '',
  );

  const setSelectedTab = React.useCallback(
    (...args: Parameters<typeof baseSetSelectedTab>) => {
      trackCustomEvent({
        action: 'switched_tab',
        category: 'Explorer',
      });
      baseSetSelectedTab(...args);
    },
    [baseSetSelectedTab, trackCustomEvent],
  );
  const [tabIds, setTabIds] = usePerGraphIdentifierLocalStorage({
    key: 'openTabs',
    graphIdentifier,
    stableInitialValue: STABLE_EMPTY_STRING_ARRAY,
  });

  // We do not want to update state on every key press, instead we let the
  // editor handle the state and use `useDerivedEditorValue` when we want to
  // hook into the values. This will load the initial state from local storage,
  // and write updates
  const getTabbedOperationsValues = useReadFromPerGraphIdentifierLocalStorage({
    key: 'tabbedOperationValues',
    graphIdentifier,
    stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
  });
  const [initialTabbedOperationsValues, setInitialTabbedOperationsValues] =
    React.useState(() => getTabbedOperationsValues());

  // We want to track a bunch of things about the tab state when explorer first
  // loads, hence all the refs usage. Specifically we want to track:
  // - How many tabs were loaded
  // - How many operations were defined in each tab
  // - How many fragments were defined in each tab
  const numberOfTabsTrackingRef = React.useRef(tabIds.length);
  const operationsTrackingRef = React.useRef(initialTabbedOperationsValues);
  const schemaTrackingRef = React.useRef(schema);
  React.useEffect(() => {
    trackCustomEvent({
      action: 'loaded_with_tabs',
      category: 'Explorer',
      value: numberOfTabsTrackingRef.current,
      legacyValueDimension: numberOfTabsTrackingRef.current,
    });
    const rootTypeNames = getRootTypeNames(schemaTrackingRef.current);
    Object.values(operationsTrackingRef.current)
      // Limit the amount of tabs we track for performance concerns
      .slice(-2)
      .forEach((operationValue) => {
        const { operationDefinitions, fragmentDefinitions } =
          getDocumentDefinitions(operationValue, rootTypeNames);
        trackCustomEvent({
          action: 'loaded_tab_with_operations',
          category: 'Explorer',
          value: operationDefinitions.length,
          legacyValueDimension: operationDefinitions.length,
        });
        trackCustomEvent({
          action: 'loaded_tab_with_fragments',
          category: 'Explorer',
          value: fragmentDefinitions.length,
          legacyValueDimension: fragmentDefinitions.length,
        });

        let isOperationValid = true;
        try {
          parse(operationValue);
        } catch {
          isOperationValid = false;
        }
        trackCustomEvent({
          action: 'loaded_tab_with_validity',
          category: 'Explorer',
          label: `Loaded ${isOperationValid ? 'valid' : 'invalid'} document`,
        });
      });
  }, [trackCustomEvent]);

  const setTabbedOperationsValues = useWriteToPerGraphIdentifierLocalStorage({
    key: 'tabbedOperationValues',
    graphIdentifier,
    stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
  });

  const getTabbedVariableValues = useReadFromPerGraphIdentifierLocalStorage({
    key: 'tabbedVariableValues',
    graphIdentifier,
    stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
  });
  const [initialTabbedVariableValues, setInitialTabbedVariableValues] =
    React.useState(getTabbedVariableValues());
  const setTabbedVariableValues = useWriteToPerGraphIdentifierLocalStorage({
    key: 'tabbedVariableValues',
    graphIdentifier,
    stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
  });

  const getTabbedPreflightOperationScriptValues =
    useReadFromPerGraphIdentifierLocalStorage({
      key: 'tabbedScriptValues',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });

  const [
    initialTabbedPreflightOperationScriptValues,
    setInitialTabbedPreflightOperationScriptValues,
  ] = React.useState(getTabbedPreflightOperationScriptValues());
  const setTabbedPreflightOperationScriptValues =
    useWriteToPerGraphIdentifierLocalStorage({
      key: 'tabbedScriptValues',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });

  const getTabbedPostflightOperationScriptValues =
    useReadFromPerGraphIdentifierLocalStorage({
      key: 'tabbedPostflightOperationScriptValues',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });

  const [
    initialTabbedPostflightOperationScriptValues,
    setInitialTabbedPostflightOperationScriptValues,
  ] = React.useState(getTabbedPostflightOperationScriptValues());
  const setTabbedPostflightOperationScriptValues =
    useWriteToPerGraphIdentifierLocalStorage({
      key: 'tabbedPostflightOperationScriptValues',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });

  const getTabbedMockedResponseValues =
    useReadFromPerGraphIdentifierLocalStorage({
      key: 'tabbedMockedResponse',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });
  const [
    initialTabbedMockedResponseValues,
    setInitialTabbedMockedResponseValues,
  ] = React.useState(getTabbedMockedResponseValues());

  const setTabbedMockedResponseValues =
    useWriteToPerGraphIdentifierLocalStorage({
      key: 'tabbedMockedResponse',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });

  // When the tab gets switched we want to update these values, but we
  // should not update for text edits coming from the editor
  const refreshTabbedOperationInfoValues = React.useCallback(() => {
    setInitialTabbedOperationsValues(getTabbedOperationsValues());
    setInitialTabbedVariableValues(getTabbedVariableValues());
    setInitialTabbedPreflightOperationScriptValues(
      getTabbedPreflightOperationScriptValues(),
    );
    setInitialTabbedPostflightOperationScriptValues(
      getTabbedPostflightOperationScriptValues(),
    );
    setInitialTabbedMockedResponseValues(getTabbedMockedResponseValues());
  }, [
    getTabbedOperationsValues,
    getTabbedPreflightOperationScriptValues,
    getTabbedPostflightOperationScriptValues,
    getTabbedVariableValues,
    getTabbedMockedResponseValues,
  ]);

  const [tabbedDisplayNames, setTabbedDisplayNames] =
    usePerGraphIdentifierLocalStorage({
      key: 'tabbedDisplayNames',
      graphIdentifier,
      stableInitialValue: STABLE_EMPTY_STRING_OBJECT,
    });

  const setTabbedHeaderDefinitions = useWriteToPerGraphIdentifierLocalStorage({
    key: 'tabbedHeaderDefinitions',
    graphIdentifier,
    stableInitialValue: STABLE_EMPTY_HEADERS_OBJECT,
  });

  const [results, setResults] = useExplorerResponses(graphIdentifier);
  const result = selectedTab !== undefined ? results?.[selectedTab] : undefined;

  const createNewTab = React.useCallback(
    (operationState: CollectionEntryState | null) => {
      const operation = operationState?.operation ?? '';
      const variables = operationState?.variables ?? '';
      const headers = operationState?.headers ?? [];
      const preflightOperationScript =
        operationState?.preflightOperationScript ?? '';
      const postflightOperationScript =
        operationState?.postflightOperationScript ?? '';
      const mockedResponse = operationState?.mockedResponse ?? '';
      const newTabUuid = generateTabId();
      setTabbedOperationsValues((currentValues) => ({
        ...currentValues,
        [newTabUuid]: operation,
      }));
      setTabbedDisplayNames((currentValues) => ({
        ...currentValues,
        [newTabUuid]: generateOperationDisplayName(operation),
      }));
      setTabbedVariableValues((currentValues) => ({
        ...currentValues,
        [newTabUuid]: variables,
      }));
      setFileVariableValuesForTab(newTabUuid, []);
      setTabbedHeaderDefinitions((currentValues) => ({
        ...currentValues,
        [newTabUuid]: headers,
      }));
      setTabbedPreflightOperationScriptValues((currentValues) => ({
        ...currentValues,
        [newTabUuid]: preflightOperationScript,
      }));
      setTabbedPostflightOperationScriptValues((currentValues) => ({
        ...currentValues,
        [newTabUuid]: postflightOperationScript,
      }));
      setTabbedMockedResponseValues((currentValues) => {
        return {
          ...currentValues,
          [newTabUuid]: mockedResponse,
        };
      });
      setResults((currentResponses) => ({
        ...currentResponses,
        [newTabUuid]: undefined,
      }));
      setTabIds((currentTabIds) => [...currentTabIds, newTabUuid]);
      setSelectedTab(newTabUuid);
      refreshTabbedOperationInfoValues();
      trackCustomEvent({
        action: 'created_tab',
        category: 'Explorer',
      });
      return newTabUuid;
    },
    [
      setTabbedOperationsValues,
      setTabbedDisplayNames,
      setTabbedVariableValues,
      setFileVariableValuesForTab,
      setTabbedHeaderDefinitions,
      setTabbedPreflightOperationScriptValues,
      setTabbedPostflightOperationScriptValues,
      setTabbedMockedResponseValues,
      setResults,
      setTabIds,
      setSelectedTab,
      refreshTabbedOperationInfoValues,
      trackCustomEvent,
    ],
  );

  const { savedOperationLocalState, dispatchSavedOperationLocalStateAction } =
    useSavedOperationLocalState({
      createNewTab,
      setSelectedTab,
      graphIdentifier,
    });

  const getInitialOperationByTab = React.useCallback(
    (id: string) => initialTabbedOperationsValues[id],
    [initialTabbedOperationsValues],
  );

  const debouncedSetTabbedDisplayNames = React.useMemo(
    () => debounce(setTabbedDisplayNames, 1000),
    [setTabbedDisplayNames],
  );

  const setOperationByTab = React.useCallback(
    (id: string, newOperation: string) => {
      setTabbedOperationsValues((currentValues) => {
        const currentValue = currentValues[id];
        if (currentValue === undefined) {
          return currentValues;
        }
        return { ...currentValues, [id]: newOperation };
      });
      dispatchSavedOperationLocalStateAction({
        type: 'ModifyLocalOperation',
        operationTabId: id,
        collectionEntryState: { operation: newOperation },
      });
      debouncedSetTabbedDisplayNames((currentValues) => ({
        ...currentValues,
        [id]: generateOperationDisplayName(newOperation),
      }));
    },
    [
      setTabbedOperationsValues,
      debouncedSetTabbedDisplayNames,
      dispatchSavedOperationLocalStateAction,
    ],
  );

  const getInitialVariablesByTab = React.useCallback(
    (id: string) => initialTabbedVariableValues[id],
    [initialTabbedVariableValues],
  );
  const setVariablesByTab = React.useCallback(
    (id: string, newVariables: string) => {
      setTabbedVariableValues((currentValues) => {
        const currentValue = currentValues[id];
        if (currentValue === undefined) {
          return currentValues;
        }
        return { ...currentValues, [id]: newVariables };
      });
      dispatchSavedOperationLocalStateAction({
        type: 'ModifyLocalOperation',
        operationTabId: id,
        collectionEntryState: { variables: newVariables },
      });
    },
    [setTabbedVariableValues, dispatchSavedOperationLocalStateAction],
  );

  // When headers change, mark as modified
  const { tabbedHeaderDefinitions } = useHeadersManagerContext();
  React.useEffect(() => {
    if (selectedTab) {
      dispatchSavedOperationLocalStateAction({
        type: 'ModifyLocalOperation',
        operationTabId: selectedTab,
        collectionEntryState: { headers: tabbedHeaderDefinitions ?? [] },
      });
    }
  }, [
    tabbedHeaderDefinitions,
    dispatchSavedOperationLocalStateAction,
    selectedTab,
  ]);

  const getInitialPreflightOperationScriptByTab = React.useCallback(
    (id: string) => initialTabbedPreflightOperationScriptValues[id],
    [initialTabbedPreflightOperationScriptValues],
  );
  const setPreflightOperationScriptByTab = React.useCallback(
    (id: string, newScript: string) => {
      setTabbedPreflightOperationScriptValues((currentValues) => {
        const currentValue = currentValues[id];
        if (currentValue === undefined) {
          return currentValues;
        }
        return { ...currentValues, [id]: newScript };
      });
      dispatchSavedOperationLocalStateAction({
        type: 'ModifyLocalOperation',
        operationTabId: id,
        collectionEntryState: { preflightOperationScript: newScript },
      });
    },
    [
      setTabbedPreflightOperationScriptValues,
      dispatchSavedOperationLocalStateAction,
    ],
  );

  const getInitialPostflightOperationScriptByTab = React.useCallback(
    (id: string) => initialTabbedPostflightOperationScriptValues[id],
    [initialTabbedPostflightOperationScriptValues],
  );
  const setPostflightOperationScriptByTab = React.useCallback(
    (id: string, newScript: string) => {
      setTabbedPostflightOperationScriptValues((currentValues) => {
        const currentValue = currentValues[id];
        if (currentValue === undefined) {
          return currentValues;
        }
        return { ...currentValues, [id]: newScript };
      });
      dispatchSavedOperationLocalStateAction({
        type: 'ModifyLocalOperation',
        operationTabId: id,
        collectionEntryState: { preflightOperationScript: newScript },
      });
    },
    [
      setTabbedPostflightOperationScriptValues,
      dispatchSavedOperationLocalStateAction,
    ],
  );

  const getInitialMockedResponseByTab = React.useCallback(
    (id: string) => initialTabbedMockedResponseValues[id],
    [initialTabbedMockedResponseValues],
  );
  const setMockedResponseByTab = React.useCallback(
    (id: string, newMockedResponse: string) => {
      setTabbedMockedResponseValues((currentValues) => {
        const currentValue = currentValues[id];
        if (currentValue === undefined) {
          return currentValues;
        }
        return { ...currentValues, [id]: newMockedResponse };
      });
      dispatchSavedOperationLocalStateAction({
        type: 'ModifyLocalOperation',
        operationTabId: id,
        collectionEntryState: { variables: newMockedResponse },
      });
    },
    [setTabbedMockedResponseValues, dispatchSavedOperationLocalStateAction],
  );

  const dispatchResultAction = React.useCallback(
    (action: ResultAction) =>
      setResults((currentResults) => {
        if (!selectedTab) {
          return currentResults;
        }

        const currentResult = currentResults[selectedTab];
        switch (action.type) {
          case 'setResult':
            return {
              ...currentResults,
              [selectedTab]: action.result,
            };
          case 'setLoading':
            return {
              ...currentResults,
              [selectedTab]:
                currentResult?.type === 'loading'
                  ? currentResult
                  : { type: 'loading', previousResponse: currentResult },
            };
          case 'resetToLastResult':
            return {
              ...currentResults,
              [selectedTab]:
                currentResult?.type === 'loading'
                  ? currentResult.previousResponse
                  : currentResult,
            };
          default:
            assertUnreachable(action);
        }
      }),
    [selectedTab, setResults],
  );

  const closeTab = React.useCallback(
    (tabId: string) => {
      setTabbedOperationsValues((currentValues) => omit(currentValues, tabId));
      setTabbedDisplayNames((currentValues) => omit(currentValues, tabId));
      setTabbedVariableValues((currentValues) => omit(currentValues, tabId));
      setTabbedHeaderDefinitions((currentValues) => omit(currentValues, tabId));
      setTabbedPreflightOperationScriptValues((currentValues) =>
        omit(currentValues, tabId),
      );
      setTabbedPostflightOperationScriptValues((currentValues) =>
        omit(currentValues, tabId),
      );
      setTabbedMockedResponseValues((currentValues) =>
        omit(currentValues, tabId),
      );
      setResults((currentResponses) => omit(currentResponses, tabId));
      dispatchSavedOperationLocalStateAction({
        type: 'CloseOperation',
        operationTabId: tabId,
      });
      setTabIds((currentTabIds) =>
        currentTabIds.filter((currentOpenTab) => currentOpenTab !== tabId),
      );
      if (selectedTab === tabId) {
        const openIndex = selectedTab ? tabIds.indexOf(selectedTab) : -1;
        setSelectedTab(
          tabIds[openIndex === -1 ? 0 : openIndex === 0 ? 1 : openIndex - 1],
        );
      }
      refreshTabbedOperationInfoValues();
      trackCustomEvent({
        action: 'closed_tab',
        category: 'Explorer',
      });
    },
    [
      setTabbedOperationsValues,
      setTabbedDisplayNames,
      setTabbedVariableValues,
      setTabbedHeaderDefinitions,
      setTabbedPreflightOperationScriptValues,
      setTabbedPostflightOperationScriptValues,
      setTabbedMockedResponseValues,
      setResults,
      dispatchSavedOperationLocalStateAction,
      setTabIds,
      selectedTab,
      refreshTabbedOperationInfoValues,
      trackCustomEvent,
      tabIds,
      setSelectedTab,
    ],
  );

  const { currentExplorerRouteConfig } = useInternalGraphLinking();
  const {
    document,
    variables,
    headers,
    preflightOperationScript,
    postflightOperationScript,
    defaultDocument,
    defaultVariables,
    defaultHeaders,
    defaultCollectionEntryId,
    defaultCollectionId,
  } = useRouteParams(currentExplorerRouteConfig);
  const hasUrlTabState = Boolean(
    document ||
      variables ||
      headers ||
      preflightOperationScript ||
      postflightOperationScript ||
      // We include embedded defaults here to prevent a race condition where the
      // default tab is opened before we have the embedded overrideDefaultTabState
      defaultDocument ||
      defaultVariables ||
      defaultHeaders ||
      defaultCollectionEntryId ||
      defaultCollectionId,
  );

  // If no operation is open and nothing is in the url, create a new
  // tab with a default operation
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [headersFromSupergraphCreation, setInitialSupergraphCreationHeaders] =
    usePerGraphIdentifierLocalStorage({
      graphIdentifier: localStorageGraphIdentifier,
      key: 'initialSupergraphCreationHeaders',
      stableInitialValue: INITIAL_HEADERS,
    });
  const { handleOpenSavedOperation } = useHandleOpenSavedOperation(
    dispatchSavedOperationLocalStateAction,
  );

  // Since the effect to open a default tab can be async,
  // we don't want to trigger it twice. When opening is in progress, this
  // should be true.
  const [openingDefaultTabLoading, setOpeningDefaultTabLoading] =
    useState(false);
  React.useEffect(() => {
    const openSavedOperationOrDefaultTab = async ({
      collectionEntryId,
      collectionId,
      sharedCollectionOriginVariantRef,
    }: CollectionEntryIdState &
      Pick<SharedCollectionEntryState, 'sharedCollectionOriginVariantRef'>) => {
      const { success } = await handleOpenSavedOperation({
        collectionEntryId,
        collectionId,
        sharedCollectionOriginVariantRef,
      });
      if (!success) {
        createNewTab({
          ...(await getDefaultTabState(schema)),
        });
      }
      setOpeningDefaultTabLoading(false);
    };

    if (!selectedTab && !hasUrlTabState && !openingDefaultTabLoading) {
      setOpeningDefaultTabLoading(true);
      if (
        explorerBehavioralFlags.overrideDefaultTabState &&
        'collectionId' in explorerBehavioralFlags.overrideDefaultTabState
      ) {
        openSavedOperationOrDefaultTab({
          ...explorerBehavioralFlags.overrideDefaultTabState,
          // overrideDefaultTabState is for embeds, and we don't support passing a variant ref
          // in from the embeds. We will pick the first variant that this colleciton belongs to
          sharedCollectionOriginVariantRef: undefined,
        });
      } else {
        const openDefaultTab = async (
          tabState: CollectionEntryState | undefined,
        ) => {
          const defaultTabState =
            tabState ?? (await getDefaultTabState(schema));
          // If we are coming to the Explorer for the first time since making a
          // new cloud graph, we want to populate the headers used in introspection
          // to set up that graph.
          createNewTab({
            ...defaultTabState,
            headers: equals(headersFromSupergraphCreation, INITIAL_HEADERS)
              ? defaultTabState.headers
              : headersFromSupergraphCreation,
          });
          setOpeningDefaultTabLoading(false);
        };
        openDefaultTab(explorerBehavioralFlags.overrideDefaultTabState);
      }
      setInitialSupergraphCreationHeaders(INITIAL_HEADERS);
    }
  }, [
    selectedTab,
    createNewTab,
    explorerBehavioralFlags,
    schema,
    hasUrlTabState,
    headersFromSupergraphCreation,
    setInitialSupergraphCreationHeaders,
    handleOpenSavedOperation,
    openingDefaultTabLoading,
  ]);

  const editorTabState = React.useMemo(
    (): EditorTabState => ({
      tabIds,
      setTabIds,
      createNewTab,
      closeTab,
      setSelectedTab,
      selectedTab,
      getInitialOperationByTab,
      setOperationByTab,
      getInitialVariablesByTab,
      setVariablesByTab,
      tabbedFileVariableValues: tabbedFileVariables,
      setFileVariableValuesForTab,
      getInitialPreflightOperationScriptByTab,
      setPreflightOperationScriptByTab,
      result,
      dispatchResultAction,
      dispatchSavedOperationLocalStateAction,
      docsPath,
      baseDispatchDocsNavAction,
      savedOperationLocalState,
      getInitialPostflightOperationScriptByTab,
      setPostflightOperationScriptByTab,
      getInitialMockedResponseByTab,
      setMockedResponseByTab,
    }),
    [
      tabIds,
      setTabIds,
      createNewTab,
      closeTab,
      setSelectedTab,
      selectedTab,
      getInitialOperationByTab,
      setOperationByTab,
      getInitialVariablesByTab,
      setVariablesByTab,
      tabbedFileVariables,
      setFileVariableValuesForTab,
      getInitialPreflightOperationScriptByTab,
      setPreflightOperationScriptByTab,
      result,
      dispatchResultAction,
      dispatchSavedOperationLocalStateAction,
      docsPath,
      baseDispatchDocsNavAction,
      savedOperationLocalState,
      getInitialPostflightOperationScriptByTab,
      setPostflightOperationScriptByTab,
      getInitialMockedResponseByTab,
      setMockedResponseByTab,
    ],
  );

  return (
    <EditorTabStateContext.Provider value={editorTabState}>
      <TabbedSavedOperationLocalStateContext.Provider
        value={savedOperationLocalState}
      >
        <TabbedDisplayNamesContext.Provider value={tabbedDisplayNames}>
          {children}
        </TabbedDisplayNamesContext.Provider>
      </TabbedSavedOperationLocalStateContext.Provider>
    </EditorTabStateContext.Provider>
  );
};
